/**
 * Demand Radar — DEMO DATA (frontend-only, fully synthetic).
 *
 * This module is intentionally disconnected from the real procurement engine,
 * RPC client, and database. Everything here is hand-authored fixture data used
 * to drive a self-contained product demo: a queue of "demand" needing to be
 * filled (sales orders + forecast deficits) and a per-item detail page with a
 * forecast chart and a scripted AI-agent walkthrough.
 *
 * Nothing in here should ever be imported by production procurement code.
 */

export type Urgency = 'critical' | 'high' | 'medium' | 'low';
export type DemandSource = 'sales-order' | 'forecast-deficit';
export type RecommendedPlay = 'transfer' | 'buy';

/** A single point on the forecast chart. */
export interface ForecastPoint {
  /** Short month label, e.g. "Apr '26". */
  month: string;
  /** Actual recorded demand (history months only). */
  actual: number | null;
  /** Middle-of-the-road projection (future months only). */
  realistic: number | null;
  /** Upper-bound projection — strong demand (future months only). */
  optimistic: number | null;
  /** Lower-bound projection — soft demand (future months only). */
  pessimistic: number | null;
  /** Planned safety-stock buffer for this month — recomputed as demand grows. */
  safetyStock: number;
  /** True for the one-time outlier month (April project build). */
  isOutlier?: boolean;
  /** True for projected (future) months. */
  isFuture?: boolean;
}

export interface TransferScenario {
  fromWarehouse: string;
  toWarehouse: string;
  /** Why this warehouse is a valid source (sibling / shared hub). */
  relationship: string;
  surplusUnits: number;
  /** Units over the source warehouse's own max. */
  overMaxUnits: number;
  transferUnits: number;
  etaDays: number;
  vendorLeadDays: number;
  /** Internal freight only — no purchase cost. */
  internalFreight: number;
  unitCost: number;
}

export interface BuyScenario {
  vendor: string;
  unitCost: number;
  units: number;
  /** Order value before adding the bundle partner. */
  baseValue: number;
  /** Vendor's free-freight threshold. */
  freeFreightThreshold: number;
  /** Freight charged if the order stays below the threshold. */
  freightIfBelow: number;
  leadDays: number;
  bundle: {
    sku: string;
    name: string;
    units: number;
    unitCost: number;
    /** Days until this partner item was due for reorder anyway. */
    dueInDays: number;
  };
}

/** One scripted line in a canned vendor negotiation. */
export interface NegotiationTurn {
  role: 'agent' | 'vendor';
  text: string;
}

/** A vendor that can fill a multi-sourced SKU; the agent negotiates with each. */
export interface CandidateVendor {
  id: string;
  name: string;
  /** Current/list unit price before negotiation. */
  listPrice: number;
  leadDays: number;
  /** 'real' places an actual Vapi call; 'canned' animates a scripted negotiation. */
  mode: 'real' | 'canned';
  /** Negotiated unit price revealed when the lane ends (preset for the demo). */
  agreedPrice: number;
  /** This vendor's own free-freight threshold (MOV). */
  freeFreightThreshold: number;
  /** Freight charged when the order value is under this vendor's MOV. */
  freightFee: number;
  /** Scripted transcript for canned lanes (and a fallback for the real one). */
  turns: NegotiationTurn[];
}

/** Multi-vendor sourcing + negotiation plan for a SKU. */
export interface SourcingPlan {
  /** The agent's per-unit target. */
  targetPrice: number;
  vendors: CandidateVendor[];
}

export type AgentWidget =
  | 'outlier-chip'
  | 'transfer-card'
  | 'freight-bar'
  | 'compare'
  | 'negotiate';

/** One streamed message from the agent. Text types out, then the widget mounts. */
export interface AgentBeat {
  text: string;
  widget?: AgentWidget;
}

export interface DemandItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  warehouse: string;
  urgency: Urgency;
  demandSource: DemandSource;
  /** Human-readable source, e.g. "SO #48201 · Acme Industrial". */
  sourceRef: string;
  needByDays: number;
  qtyShort: number;
  uom: string;
  unitCost: number;
  onHand: number;
  reserved: number;
  /** Expected replenishment lead time, in days. */
  leadTimeDays: number;
  /** How that lead time was derived (demo flavor for the empirical-lead-time story). */
  leadTimeBasis: string;
  /** Buffer stock the planner wants to keep on hand to cover demand/lead-time variability. */
  safetyStock: number;
  /** Purchasing lot multiple — this item is ordered in packs/cases of this size. */
  packSize: number;
  recommended: RecommendedPlay;
  /** Multi-vendor sourcing plan — enables the "negotiate across vendors" flow. */
  sourcing?: SourcingPlan;
  forecast: ForecastPoint[];
  /** Plain-English summary of the forecast math, shown under the chart. */
  forecastSummary: string;
  scenarios: {
    transfer?: TransferScenario;
    buy?: BuyScenario;
  };
  agentScript: AgentBeat[];
}

export const URGENCY_META: Record<
  Urgency,
  { label: string; dot: string; badge: string; order: number }
> = {
  critical: {
    label: 'Critical',
    dot: 'bg-red-500',
    badge: 'border-red-300 bg-red-50 text-red-700',
    order: 0,
  },
  high: {
    label: 'High',
    dot: 'bg-amber-500',
    badge: 'border-amber-300 bg-amber-50 text-amber-700',
    order: 1,
  },
  medium: {
    label: 'Medium',
    dot: 'bg-sky-500',
    badge: 'border-sky-300 bg-sky-50 text-sky-700',
    order: 2,
  },
  low: {
    label: 'Low',
    dot: 'bg-slate-400',
    badge: 'border-slate-300 bg-slate-50 text-slate-600',
    order: 3,
  },
};

export const available = (item: DemandItem): number =>
  item.onHand - item.reserved;

export const itemValue = (item: DemandItem): number =>
  item.qtyShort * item.unitCost;

export const roundUpToPack = (units: number, packSize: number): number =>
  Math.ceil(units / packSize) * packSize;

/** Recommended order quantity: the shortfall rounded up to a whole pack/lot. */
export const restockQty = (item: DemandItem): number =>
  roundUpToPack(item.qtyShort, item.packSize);

// --- Forecast series builders -------------------------------------------------

const HISTORY_MONTHS = [
  "Jul '25",
  "Aug '25",
  "Sep '25",
  "Oct '25",
  "Nov '25",
  "Dec '25",
  "Jan '26",
  "Feb '26",
  "Mar '26",
  "Apr '26",
  "May '26",
  "Jun '26",
];

const FUTURE_MONTHS = [
  "Jul '26",
  "Aug '26",
  "Sep '26",
  "Oct '26",
  "Nov '26",
  "Dec '26",
];

/**
 * Build a forecast series with an optional one-time outlier in the history.
 *
 * - history: recorded actuals (the outlier month, if any, stays in the data but
 *   is flagged so the chart can render it red and exclude it from the baseline)
 * - future: three layered projections — `realistic` (the baseline trend), plus
 *   an `optimistic`/`pessimistic` band that widens with the horizon.
 */
function buildForecast(opts: {
  history: number[]; // 12 actuals, Jul'25..Jun'26
  outlierIndex?: number; // index into history that is the one-time spike
  realisticStart: number; // first projected month, baseline trend
  realisticStep: number; // monthly trend for the realistic line
  baseSpread: number; // +/- fraction for opt/pess in the first month
  spreadGrowth: number; // how much the band widens each month
  currentSafetyStock: number; // buffer at "now" (the history/forecast boundary)
  safetyGrowth: number; // monthly fractional growth of the buffer as demand rises
}): ForecastPoint[] {
  // Safety stock is recomputed as demand grows: anchored to its current value
  // at the present (last history month, index 11) and compounding outward, so
  // the buffer was lower in the past and climbs across the forecast horizon.
  const PRESENT_INDEX = 11;
  const safetyAt = (globalIndex: number): number =>
    Math.round(
      opts.currentSafetyStock *
        Math.pow(1 + opts.safetyGrowth, globalIndex - PRESENT_INDEX),
    );

  const points: ForecastPoint[] = opts.history.map((actual, i) => ({
    month: HISTORY_MONTHS[i],
    actual,
    realistic: null,
    optimistic: null,
    pessimistic: null,
    safetyStock: safetyAt(i),
    isOutlier: i === opts.outlierIndex,
  }));

  FUTURE_MONTHS.forEach((month, i) => {
    const realistic = Math.round(opts.realisticStart + opts.realisticStep * i);
    const spread = opts.baseSpread + opts.spreadGrowth * i;
    points.push({
      month,
      actual: null,
      realistic,
      optimistic: Math.round(realistic * (1 + spread)),
      pessimistic: Math.round(realistic * (1 - spread)),
      safetyStock: safetyAt(opts.history.length + i),
      isFuture: true,
    });
  });

  return points;
}

// --- The hero item: full scripted story --------------------------------------

const hydraulicCoupler: DemandItem = {
  id: 'hqc-0500',
  sku: 'HQC-0500',
  name: 'Hydraulic Quick Coupler — 1/2" NPT, Flat-Face',
  category: 'Hydraulics / Fittings',
  warehouse: 'Dallas DC',
  urgency: 'critical',
  demandSource: 'sales-order',
  sourceRef: 'SO #48201 · Acme Industrial',
  needByDays: 2,
  qtyShort: 120,
  uom: 'EA',
  unitCost: 36,
  onHand: 64,
  reserved: 184,
  leadTimeDays: 14,
  leadTimeBasis: 'Measured · avg of last 6 receipts (flat default was 21d)',
  safetyStock: 80,
  packSize: 6,
  recommended: 'transfer',
  forecast: buildForecast({
    history: [88, 92, 96, 90, 101, 84, 97, 109, 94, 612, 103, 112],
    outlierIndex: 9, // Apr '26
    realisticStart: 112,
    realisticStep: 8,
    baseSpread: 0.08,
    spreadGrowth: 0.025,
    currentSafetyStock: 80,
    safetyGrowth: 0.035,
  }),
  forecastSummary:
    'Baseline demand about 95 units a month, trending +8%. The April ’26 spike (612 units) was excluded as a one-time project build — keeping it would inflate the forecast ~50% and drive chronic overstock. Realistic next-month forecast is 112 units; the optimistic/pessimistic band widens with the horizon.',
  scenarios: {
    transfer: {
      fromWarehouse: 'Fort Worth Branch',
      toWarehouse: 'Dallas DC',
      relationship: 'Sibling branch · same hub (North Texas)',
      surplusUnits: 340,
      overMaxUnits: 220,
      transferUnits: 120,
      etaDays: 2,
      vendorLeadDays: 14,
      internalFreight: 0,
      unitCost: 36,
    },
    buy: {
      vendor: 'MRO Supply Co.',
      unitCost: 36,
      units: 120,
      baseValue: 4320,
      freeFreightThreshold: 5000,
      freightIfBelow: 385,
      leadDays: 14,
      bundle: {
        sku: 'BRE-0090',
        name: 'Brass Elbow 90° — 3/4"',
        units: 80,
        unitCost: 11.5,
        dueInDays: 9,
      },
    },
  },
  agentScript: [
    {
      text: "Here's what's driving this. Order SO #48201 from Acme needs 120 couplers in 2 days, but Dallas is only sitting on 64 on-hand with 184 already reserved — so you're 120 short and it's hard-committed.",
    },
    {
      text: 'Before I forecast, I cleaned the history. Average monthly demand is about 95 units — except April ’26, which jumped to 612. That was a single one-time project build for the Riverside Stadium job, not recurring demand. I excluded it from the baseline.',
      widget: 'outlier-chip',
    },
    {
      text: "If I'd left it in, the average gets pulled to about 138 a month and the forecast inflates ~50% — you'd over-order and carry dead stock for months. Cleaned up, the adjusted forecast is 112 units next month, trending +8%.",
    },
    {
      text: "Now, how to fill it. Option 1 — don't buy at all. Fort Worth is a sibling branch on the same North Texas hub and it's overstocked: 340 on hand, 220 over its own max. I'd transfer 120 to Dallas. Arrives in ~2 days vs a 14-day vendor lead, at zero purchase cost, and it relieves Fort Worth's overstock.",
      widget: 'transfer-card',
    },
    {
      text: "Option 2 — if you'd rather buy from MRO Supply: 120 units is $4,320, but they give free freight over $5,000, so you'd eat $385 in freight. I found Brass Elbow 90° — also in deficit and due to reorder in 9 days anyway. Adding 80 of them brings the order to $5,240: free freight, and you get ahead on the elbow.",
      widget: 'freight-bar',
    },
    {
      text: 'My call: transfer. It’s faster, it’s free, and it fixes an overstock you’d otherwise have to deal with later. Buy-and-bundle is the better move only if Fort Worth needs to hold its stock. Here they are side by side.',
      widget: 'compare',
    },
  ],
};

// --- A second full story: a forecast-deficit item ----------------------------

const brassElbow: DemandItem = {
  id: 'bre-0090',
  sku: 'BRE-0090',
  name: 'Brass Elbow 90° — 3/4" Threaded',
  category: 'Plumbing / Fittings',
  warehouse: 'Dallas DC',
  urgency: 'medium',
  demandSource: 'forecast-deficit',
  sourceRef: 'Forecast deficit',
  needByDays: 9,
  qtyShort: 80,
  uom: 'EA',
  unitCost: 11.5,
  onHand: 140,
  reserved: 96,
  leadTimeDays: 12,
  leadTimeBasis: 'Measured · avg of last 8 receipts',
  safetyStock: 120,
  packSize: 10,
  recommended: 'buy',
  forecast: buildForecast({
    history: [210, 198, 225, 240, 219, 205, 232, 248, 236, 251, 244, 258],
    realisticStart: 262,
    realisticStep: 6,
    baseSpread: 0.07,
    spreadGrowth: 0.02,
    currentSafetyStock: 120,
    safetyGrowth: 0.02,
  }),
  forecastSummary:
    'Steady mover, about 230 units a month trending +3%. No outliers to exclude. Available stock drops below the level that triggers a reorder in about 9 days — this is the natural bundle partner for the coupler PO.',
  scenarios: {
    buy: {
      vendor: 'MRO Supply Co.',
      unitCost: 11.5,
      units: 80,
      baseValue: 920,
      freeFreightThreshold: 5000,
      freightIfBelow: 210,
      leadDays: 12,
      bundle: {
        sku: 'HQC-0500',
        name: 'Hydraulic Quick Coupler 1/2"',
        units: 120,
        unitCost: 36,
        dueInDays: 2,
      },
    },
  },
  agentScript: [
    {
      text: "This one isn't a firm order yet — it's a forecast deficit. Available stock (44 after reservations) drops below the level that triggers a reorder in about 9 days at the current run rate.",
    },
    {
      text: 'The history is clean — no outliers to strip out. It’s a steady mover at about 230 a month trending up slightly, so I’m projecting 262 next month.',
      widget: 'outlier-chip',
    },
    {
      text: "On its own, 80 units from MRO Supply is only $920 — nowhere near the $5,000 free-freight tier, so you'd pay $210 freight on a small order. Don't order it alone.",
      widget: 'freight-bar',
    },
    {
      text: 'Instead, ride along on the coupler PO that’s already going to MRO Supply. Together they clear $5,000 — free freight on both, one inbound receipt, and you’re ahead of the curve here.',
      widget: 'compare',
    },
  ],
};

// --- Lighter-weight queue items (still navigable) ----------------------------

const galvBracket: DemandItem = {
  id: 'gvb-2210',
  sku: 'GVB-2210',
  name: 'Galvanized Mounting Bracket — Heavy Duty',
  category: 'Structural / Hardware',
  warehouse: 'Phoenix DC',
  urgency: 'critical',
  demandSource: 'sales-order',
  sourceRef: 'SO #48255 · Sunbelt Mechanical',
  needByDays: 1,
  qtyShort: 45,
  uom: 'EA',
  unitCost: 22.4,
  onHand: 12,
  reserved: 57,
  leadTimeDays: 10,
  leadTimeBasis: 'Measured · avg of last 5 receipts',
  safetyStock: 30,
  packSize: 6,
  recommended: 'transfer',
  forecast: buildForecast({
    history: [60, 55, 62, 58, 64, 51, 59, 66, 61, 63, 57, 65],
    realisticStart: 66,
    realisticStep: 2,
    baseSpread: 0.06,
    spreadGrowth: 0.015,
    currentSafetyStock: 30,
    safetyGrowth: 0.015,
  }),
  forecastSummary:
    'Stable about 60 units a month. No outliers. A same-day customer order with almost no stock on hand — transfer is the only path that hits the deadline.',
  scenarios: {
    transfer: {
      fromWarehouse: 'Tucson Branch',
      toWarehouse: 'Phoenix DC',
      relationship: 'Sibling branch · same hub (Arizona)',
      surplusUnits: 130,
      overMaxUnits: 70,
      transferUnits: 45,
      etaDays: 1,
      vendorLeadDays: 10,
      internalFreight: 0,
      unitCost: 22.4,
    },
  },
  agentScript: [
    {
      text: 'Order SO #48255 needs 45 brackets tomorrow and Phoenix is effectively out — 12 on hand against 57 reserved.',
    },
    {
      text: 'History is clean at about 60 a month, nothing to exclude. The real question is just speed.',
      widget: 'outlier-chip',
    },
    {
      text: 'Tucson is a sibling branch with 130 on hand, 70 over max. Transfer 45 — it can be here in 1 day. A vendor PO is 10 days; it would miss the order entirely.',
      widget: 'transfer-card',
    },
  ],
};

const copperLug: DemandItem = {
  id: 'cul-0033',
  sku: 'CUL-0033',
  name: 'Copper Compression Lug — 4 AWG',
  category: 'Electrical',
  warehouse: 'Dallas DC',
  urgency: 'high',
  demandSource: 'forecast-deficit',
  sourceRef: 'Forecast deficit',
  needByDays: 18,
  qtyShort: 300,
  uom: 'EA',
  unitCost: 3.1,
  onHand: 520,
  reserved: 410,
  leadTimeDays: 7,
  leadTimeBasis: 'Measured · avg of last 10 receipts',
  safetyStock: 180,
  packSize: 50,
  recommended: 'buy',
  sourcing: {
    targetPrice: 2.8,
    vendors: [
      {
        id: 'lone-star',
        name: 'Lone Star Electric Supply',
        listPrice: 3.1,
        leadDays: 7,
        mode: 'real',
        agreedPrice: 2.85,
        freeFreightThreshold: 750,
        freightFee: 60,
        turns: [
          {
            role: 'agent',
            text: "We're placing 300 units of CUL-0033 today — what's your best per-unit price?",
          },
          { role: 'vendor', text: 'For 300 units I can do $2.85.' },
          { role: 'agent', text: "Appreciate it — I'll confirm by email." },
        ],
      },
      {
        id: 'graybar',
        name: 'Graybar',
        listPrice: 3.05,
        leadDays: 9,
        mode: 'canned',
        agreedPrice: 2.91,
        freeFreightThreshold: 1000,
        freightFee: 95,
        turns: [
          {
            role: 'agent',
            text: '300 units of CUL-0033 — your list is $3.05. Can you do better for a firm order today?',
          },
          { role: 'vendor', text: 'For 300 units I can come down to $2.95.' },
          {
            role: 'agent',
            text: "I've got better on the table elsewhere — can you get under $2.90?",
          },
          {
            role: 'vendor',
            text: 'Best I can do is $2.91 a unit, 9-day lead.',
          },
          { role: 'agent', text: 'Understood — thanks.' },
        ],
      },
      {
        id: 'border-states',
        name: 'Border States',
        listPrice: 3.2,
        leadDays: 8,
        mode: 'canned',
        agreedPrice: 2.95,
        freeFreightThreshold: 1500,
        freightFee: 80,
        turns: [
          {
            role: 'agent',
            text: 'Can you sharpen $3.20 on 300 units of CUL-0033 today?',
          },
          { role: 'vendor', text: 'Best I can do is $2.95.' },
          { role: 'agent', text: 'Understood — appreciate it.' },
        ],
      },
    ],
  },
  forecast: buildForecast({
    history: [400, 380, 410, 460, 520, 610, 540, 480, 450, 470, 500, 560],
    realisticStart: 600,
    realisticStep: 25,
    baseSpread: 0.1,
    spreadGrowth: 0.03,
    currentSafetyStock: 180,
    safetyGrowth: 0.04,
  }),
  forecastSummary:
    'Seasonal ramp into summer, about 480 units a month and climbing. Plenty of runway (18 days) — a routine buy, good candidate to consolidate onto a larger electrical PO.',
  scenarios: {
    buy: {
      vendor: 'Graybar',
      unitCost: 3.1,
      units: 300,
      baseValue: 930,
      freeFreightThreshold: 2500,
      freightIfBelow: 95,
      leadDays: 7,
      bundle: {
        sku: 'CUL-0061',
        name: 'Copper Lug — 2 AWG',
        units: 250,
        unitCost: 4.2,
        dueInDays: 15,
      },
    },
  },
  agentScript: [
    {
      text: 'Routine one — a seasonal ramp, not a fire. You have 18 days of runway before the deficit bites.',
    },
    {
      text: 'No outliers; demand is just climbing into summer. Projecting about 600 a month.',
      widget: 'outlier-chip',
    },
    {
      text: "This lug is multi-sourced across 3 distributors. Let me call all three, negotiate the unit price, and rank them by landed cost — each vendor's freight and order minimum included, since the cheapest sticker isn't always the cheapest delivered.",
      widget: 'negotiate',
    },
  ],
};

const ptfeTape: DemandItem = {
  id: 'ptf-0012',
  sku: 'PTF-0012',
  name: 'PTFE Thread Seal Tape — 1/2" x 520"',
  category: 'Consumables',
  warehouse: 'Phoenix DC',
  urgency: 'low',
  demandSource: 'forecast-deficit',
  sourceRef: 'Forecast deficit',
  needByDays: 24,
  qtyShort: 900,
  uom: 'RL',
  unitCost: 0.62,
  onHand: 1800,
  reserved: 1450,
  leadTimeDays: 9,
  leadTimeBasis: 'Vendor standard',
  safetyStock: 320,
  packSize: 100,
  recommended: 'buy',
  forecast: buildForecast({
    history: [820, 790, 810, 850, 800, 780, 830, 870, 840, 860, 845, 880],
    realisticStart: 885,
    realisticStep: 4,
    baseSpread: 0.05,
    spreadGrowth: 0.01,
    currentSafetyStock: 320,
    safetyGrowth: 0.012,
  }),
  forecastSummary:
    'High-volume, low-cost consumable. Extremely steady at about 830 a month. No urgency — buy on the next scheduled consumables PO.',
  scenarios: {
    buy: {
      vendor: 'MRO Supply Co.',
      unitCost: 0.62,
      units: 900,
      baseValue: 558,
      freeFreightThreshold: 5000,
      freightIfBelow: 120,
      leadDays: 9,
      bundle: {
        sku: 'HQC-0500',
        name: 'Hydraulic Quick Coupler 1/2"',
        units: 120,
        unitCost: 36,
        dueInDays: 2,
      },
    },
  },
  agentScript: [
    {
      text: 'Lowest priority on the board — 24 days of cover left on a cheap consumable.',
    },
    {
      text: 'Dead-steady history, nothing to exclude. About 830 a month.',
      widget: 'outlier-chip',
    },
    {
      text: 'Too small to ship on its own. Tack it onto the next MRO Supply order so it never costs freight.',
      widget: 'freight-bar',
    },
  ],
};

export const DEMAND_ITEMS: DemandItem[] = [
  hydraulicCoupler,
  galvBracket,
  copperLug,
  brassElbow,
  ptfeTape,
];

export const getItem = (id: string): DemandItem | undefined =>
  DEMAND_ITEMS.find((item) => item.id === id);

export const WAREHOUSES = Array.from(
  new Set(DEMAND_ITEMS.map((i) => i.warehouse)),
).sort();
